import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { heartBeatOpts, wsUrl } from './config';
import SocketLink from './socket';
import { i18nt } from '@/i18n';
// import { refreshToken } from '@/apis/user';
import { useTokenStore, useUserStore } from '@/store';
import { useChatHistoryStore } from '@/store/chatHistory';
import { toPage } from '@/utils';
import { showToast } from 'vant';

/** 发送失败原因枚举，对应不同 alert 提示语 */
export type SendFailReason =
  | 'timeout' // 服务端响应超时
  | 'nosocket' // WebSocket 未初始化
  | 'connect_timeout' // 连接建立超时
  | 'connect_error' // 连接 error 事件
  | 'connect_closed' // 连接 close 事件
  | 'send_error' // ws.send 同步抛错
  | 'disconnect'; // disconnectWs 主动清空 pending

const SEND_TIMEOUT = 15000;
const CONNECT_TIMEOUT = 10000;
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY = 3000;

let socket: SocketLink | undefined;
let connectPromise: Promise<WebSocket> | null = null;
let isRefreshingToken = false;
let isManualDisconnect = false;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempts = 0;
let resumeListenerRegistered = false;

// 解决重连问题
let reconnectPromise: Promise<void> | null = null;
let activeReconnectIsForce = false;
let forceReconnectPending = false;

const handles = {
  onMessage: (_res: unknown) => {},
};

type PendingItem = {
  resolve: (data: unknown) => void;
  reject: (err: unknown) => void;
  timer: ReturnType<typeof setTimeout>;
};

type QueuedSend = {
  payload: unknown;
  resolveSend: () => void;
  rejectSend: (err: unknown) => void;
};

const pendingByKey = new Map<string, PendingItem>();
const sendQueue: QueuedSend[] = [];

const sendError = (reason: SendFailReason = 'timeout') => ({
  msg: i18nt('fwxycs'),
  duration: 3000,
  reason,
});

function getPendingKey(data: { sessionId?: string; messageType?: string }) {
  return `${data.sessionId}:${data.messageType || 'text'}`;
}

function rejectAllPending(reason = sendError()) {
  pendingByKey.forEach((pending) => {
    clearTimeout(pending.timer);
    pending.reject(reason);
  });
  pendingByKey.clear();
}

function rejectSendQueue(reason = sendError()) {
  while (sendQueue.length) {
    sendQueue.shift()?.rejectSend(reason);
  }
}

function resetConnectPromise() {
  connectPromise = null;
}

function clearReconnectTimer() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function resolvePending(data: Record<string, unknown>) {
  if (!data.sessionId) return;
  const key = getPendingKey(data as { sessionId: string; messageType?: string });
  const pending = pendingByKey.get(key);
  if (!pending) return;
  clearTimeout(pending.timer);
  pendingByKey.delete(key);
  pending.resolve(data);
}

export const getWsUrl = async (): Promise<string> => {
  const socketUrl = typeof wsUrl === 'function' ? await wsUrl() : wsUrl;
  return socketUrl as string;
};

const callbacks = {
  onOpenCallback(ws: WebSocket) {
    reconnectAttempts = 0;
    clearReconnectTimer();
    // ws.send(heartBeatOpts.ping);
    socket.heartBeat();
    flushSendQueue();
  },
  onMessageCallback(data: string) {
    if (data === heartBeatOpts.pong) return;

    try {
      const res = JSON.parse(data);
      if (res.data?.chatType === 'token-failed') {
        handleWsTokenFailed();
        return;
      }
      if (res.data) {
        handles.onMessage(res.data);
        resolvePending(res.data);
      }
    } catch (err) {
      console.error('WebSocket message parse error:', err, data);
    }
  },
  onCloseCallback() {
    if (isManualDisconnect || isRefreshingToken) return;
    scheduleReconnect();
  },
  onReconnectRequest() {
    if (isManualDisconnect || isRefreshingToken) return;
    reconnectWs().catch(() => scheduleReconnect());
  },
};

function scheduleReconnect() {
  if (isManualDisconnect || isRefreshingToken || !socket) return;
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return;
  if (reconnectTimer) return;

  reconnectTimer = setTimeout(async () => {
    reconnectTimer = null;
    reconnectAttempts += 1;
    try {
      await reconnectWs();
      reconnectAttempts = 0;
    } catch {
      scheduleReconnect();
    }
  }, RECONNECT_DELAY);
}

async function checkAndReconnect() {
  if (isManualDisconnect || !socket) return;
  if (socket.ws?.readyState === WebSocket.OPEN) return;
  try {
    await reconnectWs();
  } catch {
    scheduleReconnect();
  }
}

function initAppResumeListener() {
  if (resumeListenerRegistered) return;
  resumeListenerRegistered = true;

  if (Capacitor.getPlatform() === 'web') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        checkAndReconnect();
      }
    });
    return;
  }

  App.addListener('resume', () => {
    checkAndReconnect();
  });
}

async function handleWsTokenFailed() {
  const tokenStore = useTokenStore();
  // if (tokenStore.isTokenRefreshing) {
  //   isRefreshingToken = tokenStore.isTokenRefreshing;
  //   return;
  // }
  if (isRefreshingToken) return;

  isRefreshingToken = true;

  resetConnectPromise();
  try {
    await tokenStore.refreshTokenFn();
    await reconnectWs(true);
    await flushSendQueue();
  } catch {
    disconnectWs();
    const userStore = useUserStore();
    useChatHistoryStore().resetSession();
    showToast(`${i18nt('dlygq')}，${i18nt('qcxdl')}`);
    toPage('/login', false);
    await userStore.clearUser();
  } finally {
    isRefreshingToken = false;
  }
}

export function disconnectWs() {
  isManualDisconnect = true;
  clearReconnectTimer();
  resetConnectPromise();
  rejectAllPending(sendError('disconnect'));
  rejectSendQueue(sendError('disconnect'));
  socket?.close();
  reconnectAttempts = 0;

  setTimeout(() => {
    isManualDisconnect = false;
  }, 0);
}

function startWs(force = false): WebSocket {
  if (!socket) {
    throw new Error('WebSocket 未初始化，请先 connectWs');
  }

  const { ws } = socket;
  if (
    ws &&
    (ws.readyState === WebSocket.OPEN ||
      ws.readyState === WebSocket.CONNECTING)
  ) {
    return ws;
  }

  socket.initSocket(force);
  return socket.ws!;
}

function ensureWsOpen(): Promise<WebSocket> {
  let ws: WebSocket;
  try {
    ws = startWs();
  } catch {
    return Promise.reject(sendError('nosocket'));
  }

  if (ws.readyState === WebSocket.OPEN) {
    return Promise.resolve(ws);
  }

  if (connectPromise) {
    return connectPromise;
  }

  connectPromise = new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      resetConnectPromise();
      reject(sendError('connect_timeout'));
    }, CONNECT_TIMEOUT);

    const cleanup = () => {
      clearTimeout(timeout);
      ws.removeEventListener('open', onOpen);
      ws.removeEventListener('error', onError);
      ws.removeEventListener('close', onClose);
    };

    const onOpen = () => {
      cleanup();
      resetConnectPromise();
      resolve(ws);
    };

    const onError = () => {
      cleanup();
      resetConnectPromise();
      reject(sendError('connect_error'));
    };

    const onClose = () => {
      cleanup();
      resetConnectPromise();
      reject(sendError('connect_closed'));
    };

    ws.addEventListener('open', onOpen, { once: true });
    ws.addEventListener('error', onError, { once: true });
    ws.addEventListener('close', onClose, { once: true });
  });

  return connectPromise;
}

async function flushSendQueue() {
  if (!sendQueue.length) return;

  try {
    const ws = await ensureWsOpen();
    const queue = sendQueue.splice(0, sendQueue.length);
    queue.forEach((item) => {
      try {
        ws.send(JSON.stringify(item.payload));
        item.resolveSend();
      } catch (err) {
        item.rejectSend(err);
      }
    });
  } catch {
    rejectSendQueue(sendError('connect_error'));
  }
}

async function sendPayload(message: unknown): Promise<void> {
  if (isRefreshingToken) {
    return new Promise((resolve, reject) => {
      sendQueue.push({
        payload: message,
        resolveSend: resolve,
        rejectSend: reject,
      });
    });
  }

  const ws = await ensureWsOpen();
  try {
    ws.send(JSON.stringify(message));
  } catch (err) {
    throw sendError('send_error');
  }
}

export async function reconnectWs123(force = false) {
  if (!socket) return Promise.resolve();
  resetConnectPromise();
  socket.close();
  socket.socketUrl = await getWsUrl();
  startWs(force);
  await ensureWsOpen();
  await flushSendQueue();
}

// 心跳、close、前台恢复和 token 刷新可能并发调用 reconnectWs()，需要确保只有一个重连在执行。
export function reconnectWs(force = false): Promise<void> {
  if (!socket) {
    return Promise.resolve();
  }

  // 已经有重连正在执行，复用当前重连。
  if (reconnectPromise) {
    // Token 刷新要求强制更换连接。
    // 如果当前只是普通重连，则在完成后再执行一次强制重连。
    if (force && !activeReconnectIsForce) {
      forceReconnectPending = true;
    }

    return reconnectPromise.then(async () => {
      if (forceReconnectPending) {
        forceReconnectPending = false;
        await reconnectWs(true);
      }
    }).finally(() => {
      forceReconnectPending = false;
    })
  }

  activeReconnectIsForce = force;

  reconnectPromise = (async () => {
    // 普通重连不能关闭一个已经正常 OPEN 的连接。
    if (!force && socket?.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    // 先准备 URL，避免获取 URL 期间提前断开旧连接。
    const socketUrl = await getWsUrl();

    if (!socket) return;

    resetConnectPromise();
    socket.close();
    socket.socketUrl = socketUrl;

    startWs(force);
    await ensureWsOpen();
    await flushSendQueue();
  })().finally(() => {
    reconnectPromise = null;
    activeReconnectIsForce = false;
  });

  return reconnectPromise;
}

export async function connectWs({ onMessage }: { onMessage: (msg: unknown) => void }) {
  handles.onMessage = onMessage;
  initAppResumeListener();

  if (socket?.ws?.readyState === WebSocket.OPEN) {
    return socket.ws;
  }

  const socketUrl = await getWsUrl();

  if (socket) {
    resetConnectPromise();
    socket.close();
    socket.socketUrl = socketUrl;
  } else {
    socket = new SocketLink(socketUrl, callbacks, heartBeatOpts);
  }

  startWs();
  await ensureWsOpen();
  return socket.ws!;
}

export async function sendEmit(message: { data: Record<string, unknown> }) {
  const data = message.data;
  const err = sendError('timeout');

  try {
    await sendPayload(message);
  } catch (sendErr) {
    throw sendErr;
  }

  if (data.messageType === 'reading') {
    return data;
  }

  const pendingKey = getPendingKey(
    data as { sessionId: string; messageType?: string },
  );

  return new Promise((resolve, reject) => {
    const existing = pendingByKey.get(pendingKey);
    if (existing) {
      clearTimeout(existing.timer);
      pendingByKey.delete(pendingKey);
    }

    const timer = setTimeout(() => {
      pendingByKey.delete(pendingKey);
      reject(sendError('timeout'));
    }, SEND_TIMEOUT);

    pendingByKey.set(pendingKey, { resolve, reject, timer });
  });
}
